/**
 * Cross-boundary wire schema for the cross-account Open Loops read path.
 *
 * `GET /api/v4/open-loops` — one row per open loop, flattened across every
 * account the acting user owns (`getOwnedAccounts`), read from the
 * `account_intelligence` local mirror of Keystone account-state generations
 * (NOT a live Keystone call — see `AccountIntelligence`'s doc comment in
 * `prisma/schema/core.prisma`).
 *
 * Pattern followed: `src/shared/manager-deal-health/wire.ts` — Zod schemas
 * are canonical, TS types are `z.infer<typeof XSchema>` (CLAUDE.md "Schemas
 * Are the Source of Truth"). Do NOT hand-write a parallel interface for any
 * type below — `terra/no-parallel-schema-and-type` and
 * `terra/no-duplicate-schema-name` enforce this.
 *
 * `loop` is NOT redeclared here — it is `@clay-run/keystone-contract`'s
 * `OpenLoopSchema` (a `z.string()` | `OpenLoopEntrySchema` union), the same
 * contract schema the single-account `OpenLoopsSection`/`OpenLoopItem`
 * components key off of.
 */

import { z } from 'zod';
import {
  LoopSuggestionSchema,
  OpenLoopEntrySchema,
  OpenLoopSchema,
  type LoopCitation,
  type LoopInsight,
} from '@clay-run/keystone-contract';

export type OpenLoop = z.infer<typeof OpenLoopSchema>;

/**
 * The Clay person accountable for the loop moving.
 *
 * Distinct from `responsibility`, which says whose COURT the ball is in. This is
 * always one of ours, INCLUDING on a "them" loop: a loop waiting on the customer
 * still needs somebody here chasing it, and the page filters on this field.
 *
 * Both halves nullish because keystone omits the assignee rather than half-fill
 * it from a guessed address, so a loop it could not ground has no assignee.
 */
export type LoopAssignee = NonNullable<z.infer<typeof OpenLoopEntrySchema>['assignee']>;

/** A loop's assignee; null for the legacy string arm and for unassigned loops. */
export function loopAssignee(loop: OpenLoop): LoopAssignee | null {
  if (typeof loop === 'string') return null;
  return loop.assignee ?? null;
}

/**
 * "mohak.desai@clay.com" → "Mohak Desai". An address is not a name.
 *
 * Needed because Gong frequently records a party's NAME as their email address:
 * 514 of the 1,223 distinct Clay attendees in production are stored that way, so
 * without this, two of every five pills would read as an address.
 *
 * Splits on the separators an address local part actually uses and title-cases
 * each word. This mangles genuinely-mixed-case surnames ("mcdonald" → "Mcdonald")
 * — accepted, because the alternative is showing a raw address, and a slightly
 * wrong capitalisation still reads as a person.
 */
function humanizeAddress(address: string): string {
  const local = address.split('@')[0] ?? address;
  const words = local.split(/[._\-+]+/).filter((w) => w !== '');
  if (words.length === 0) return local;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * The assignee's display name, or null when there is nothing usable to show.
 *
 * Prefers a real name, then derives one from the address — so a loop still
 * labels and still filters rather than dropping out of the assignee list on one
 * missing field.
 *
 * A `name` CONTAINING AN `@` is treated as an address, not a name. Gong records
 * it that way for a large minority of parties, and passing it straight through
 * is what put "mohak.desai@clay.com" on a pill.
 *
 * Shared so the row badge and the filter bar cannot disagree about what someone
 * is called: they group on this exact string, so two implementations would
 * silently split one person into two pills.
 */
export function assigneeLabel(assignee: LoopAssignee | null | undefined): string | null {
  if (!assignee) return null;

  const name = assignee.name?.trim();
  if (name && !name.includes('@')) return name;

  const address = (name && name.includes('@') ? name : assignee.email?.trim()) ?? '';
  if (address === '') return null;
  return humanizeAddress(address);
}

// ─── Row / response ─────────────────────────────────────────────────────────

/**
 * One flattened open loop, joined with its owning account's display name
 * (from `SalesforceAccount.name` via the `AccountIntelligence.account`
 * relation) and the mirror generation's `generatedAt` freshness marker.
 * `salesforceAccountId` is the SFDC id the frontend routes on
 * (`/accounts/:accountId`) — never the internal `AccountIntelligence.id`.
 *
 * `loopHash` (per-user dismissal/feedback identity, `src/shared/open-loops/
 * loop-hash.ts`) is computed server-side from `loopItemText(loop)` — the
 * frontend never derives it itself, it only uses it as the mutation key for
 * dismiss/feedback requests.
 */
export const OpenLoopRowSchema = z
  .object({
    salesforceAccountId: z.string().min(1),
    accountName: z.string().nullable(),
    generatedAt: z.string().nullable(),
    loop: OpenLoopSchema,
    loopHash: z.string().min(1),
    /**
     * Whether this loop's account has at least one opportunity that is not closed.
     *
     * An ACCOUNT-level fact repeated on every one of its loops. Denormalised on
     * purpose: the client filters a flat row list, and a separate account map would
     * have to be kept in step with it for no gain.
     *
     * Optional so a client on an older payload still parses — `.strict()` rejects
     * unknown keys, but a MISSING key is what a version skew actually produces.
     */
    hasOpenOpportunity: z.boolean().optional(),
  })
  .strict();
export type OpenLoopRow = z.infer<typeof OpenLoopRowSchema>;

export const OpenLoopsResponseSchema = z
  .object({
    rows: z.array(OpenLoopRowSchema),
  })
  .strict();
export type OpenLoopsResponse = z.infer<typeof OpenLoopsResponseSchema>;

// ─── Per-user dismissal + feedback (identity: userId + salesforceAccountId +
// loopHash) ──────────────────────────────────────────────────────────────

/**
 * A user's up/down verdict on one loop. `null` (on the feedback request/
 * response below) means "no verdict" / "clear the existing verdict" — it is
 * never a third verdict value in its own right.
 */
export const OpenLoopFeedbackVerdictSchema = z.enum(['up', 'down']);
export type OpenLoopFeedbackVerdict = z.infer<typeof OpenLoopFeedbackVerdictSchema>;

/**
 * `GET` response for the acting user's dismissal + feedback state, scoped to
 * one account (or however the read-path service scopes it — this schema
 * doesn't encode the scope itself, just the shape). `dismissedLoopHashes` is
 * a flat array of hashes (dismissal carries no other state — it's a boolean
 * per hash), `feedback` is an array since a verdict carries a value beyond
 * presence/absence.
 */
export const OpenLoopAnnotationsResponseSchema = z
  .object({
    dismissedLoopHashes: z.array(z.string().min(1)),
    feedback: z.array(
      z
        .object({
          loopHash: z.string().min(1),
          verdict: OpenLoopFeedbackVerdictSchema,
        })
        .strict(),
    ),
  })
  .strict();
export type OpenLoopAnnotationsResponse = z.infer<typeof OpenLoopAnnotationsResponseSchema>;

/**
 * Why the user removed a loop — two distinct affordances in the UI:
 *  - `'done'`: the loop was real and got resolved. Ground truth that the
 *    account state is stale on this item — future signal for Keystone's
 *    resolution layer.
 *  - `'not_relevant'`: the loop shouldn't have surfaced at all. Quality
 *    signal about generation, not staleness.
 * Suppression behavior is identical for both; the reason is recorded for
 * analysis and eventual feedback into generation.
 */
export const OpenLoopDismissReasonSchema = z.enum(['done', 'not_relevant']);
export type OpenLoopDismissReason = z.infer<typeof OpenLoopDismissReasonSchema>;

/**
 * WHY a loop was dismissed as noise, or thumbed down — the tuning signal.
 *
 * Distinct from `OpenLoopDismissReasonSchema`, which records WHICH affordance
 * was used (done vs not-relevant). This is the follow-up detail collected by
 * the reason prompt, and it is deliberately a small closed set: the point of
 * the signal is to be COUNTED when tuning generation quality, and free text
 * alone can only be read one row at a time. `reasonNote` carries anything the
 * presets miss.
 *
 * Both are OPTIONAL everywhere (user call 2026-08-10: the prompt is
 * skippable, and the dismissal/verdict applies before it is answered) — so
 * every consumer must treat an absent code as "not given", never as a default
 * bucket. Do not add a synthetic 'unspecified' member: absence is already the
 * representation, and a member would make "skipped" indistinguishable from
 * "actively chose unspecified".
 */
export const OpenLoopReasonCodeSchema = z.enum([
  'not_a_commitment',
  'wrong_owner',
  'already_handled',
  'unclear',
  'other',
]);
export type OpenLoopReasonCode = z.infer<typeof OpenLoopReasonCodeSchema>;

/** Cap on the free-text note — a tuning hint, not a document. */
export const OPEN_LOOP_REASON_NOTE_MAX = 500;

/**
 * Cap on `loopItemText` across every request that carries it. This text is
 * stored verbatim (`OpenLoopDismissal.loopItemText`,
 * `OpenLoopFeedback.loopItemText`) and pushed to keystone
 * (`syncLoopDismissalToKeystone`/`syncLoopUndismissalToKeystone`), which
 * splices it directly into an LLM generation prompt — an unbounded string
 * here is a prompt-poisoning vector, not just a storage concern. A real loop
 * item is a short summary sentence, so 2000 chars is generous headroom, not
 * a tight fit.
 */
export const OPEN_LOOP_ITEM_TEXT_MAX = 2000;

/**
 * The optional reason detail, shared verbatim by the dismiss and feedback
 * requests so the two can never drift into different field names for the
 * same concept.
 */
const reasonDetailFields = {
  reasonCode: OpenLoopReasonCodeSchema.optional(),
  reasonNote: z.string().trim().min(1).max(OPEN_LOOP_REASON_NOTE_MAX).optional(),
} as const;

/**
 * Dismiss request: the client sends the raw loop text, never a
 * client-computed hash — the server is the sole computer of `loopHash`
 * (`hashLoopText(loopItemText(loop))`), so a stale/mismatched client-side
 * hashing implementation can never desync the identity key.
 */
export const OpenLoopDismissRequestSchema = z
  .object({
    salesforceAccountId: z.string().min(1),
    loopItemText: z.string().min(1).max(OPEN_LOOP_ITEM_TEXT_MAX),
    reason: OpenLoopDismissReasonSchema,
    ...reasonDetailFields,
  })
  .strict();
export type OpenLoopDismissRequest = z.infer<typeof OpenLoopDismissRequestSchema>;

/**
 * Undismiss (undo) request: identity only — restoring a loop needs no
 * reason, and requiring the original one would force the client to remember
 * state the server already has.
 */
export const OpenLoopUndismissRequestSchema = z
  .object({
    salesforceAccountId: z.string().min(1),
    loopItemText: z.string().min(1).max(OPEN_LOOP_ITEM_TEXT_MAX),
  })
  .strict();
export type OpenLoopUndismissRequest = z.infer<typeof OpenLoopUndismissRequestSchema>;

export const OpenLoopDismissResponseSchema = z
  .object({
    dismissed: z.boolean(),
    loopHash: z.string().min(1),
  })
  .strict();
export type OpenLoopDismissResponse = z.infer<typeof OpenLoopDismissResponseSchema>;

/**
 * Feedback request: same "client sends text, server computes hash"
 * convention as the dismiss request above. `verdict: null` clears an
 * existing verdict rather than setting one.
 */
export const OpenLoopFeedbackRequestSchema = z
  .object({
    salesforceAccountId: z.string().min(1),
    loopItemText: z.string().min(1).max(OPEN_LOOP_ITEM_TEXT_MAX),
    verdict: OpenLoopFeedbackVerdictSchema.nullable(),
    ...reasonDetailFields,
  })
  .strict();
export type OpenLoopFeedbackRequest = z.infer<typeof OpenLoopFeedbackRequestSchema>;

export const OpenLoopFeedbackResponseSchema = z
  .object({
    verdict: OpenLoopFeedbackVerdictSchema.nullable(),
    loopHash: z.string().min(1),
  })
  .strict();
export type OpenLoopFeedbackResponse = z.infer<typeof OpenLoopFeedbackResponseSchema>;

// ── Suggestions: what past closed deals say about a loop ─────────────────────

/**
 * `GET /api/v4/open-loops/suggestions` — terra's own body. Composes keystone's
 * `LoopSuggestionSchema`; keystone's storedOnly-mode `loops` / `noPrecedent`
 * are consumed by the handler (to derive `generating`), not forwarded.
 *
 * `action: null` on a suggestion is an honest "the corpus supports no action",
 * not an error state; the UI shows it as such.
 */
export const V4LoopSuggestionsResponseSchema = z
  .object({
    suggestions: z.array(LoopSuggestionSchema),
    /**
     * True while terra is generating the loops that have nothing stored yet.
     * The client shows a pending state and waits for the SSE `invalidate`
     * (`loop_suggestions_ready`) instead of polling or holding a request open.
     */
    generating: z.boolean(),
  })
  .strict();
export type V4LoopSuggestionsResponse = z.infer<typeof V4LoopSuggestionsResponseSchema>;

/** True when a citation names a deal we LOST — the evidence is a warning, not a play. */
export function isLossCitation(citation: LoopCitation): boolean {
  return citation.outcome === 'lost' || citation.outcome === 'disqualified';
}

/** True when a citation names a deal we WON — a precedent worth copying. */
export function isWinCitation(citation: LoopCitation): boolean {
  return citation.outcome === 'won';
}

/**
 * What an insight's evidence actually says: a precedent, a warning, or unlabelled.
 *
 * THREE states, not two, and the third is the whole point. `outcome` is optional
 * on the wire and keystone omits it whenever the indexed moment predates the
 * field, so "not a loss" and "a win" are different claims. A two-state test
 * conflated them and captioned lost-deal warnings "Won" — the quote said the
 * deal died and the badge said it closed.
 *
 * 'lost' wins over 'won' on a mixed set. An insight can cite several moments, and
 * one lost-deal citation is enough to make the whole thing a caution: reading a
 * mixed set as a precedent is the failure that costs something.
 */
export type InsightVerdict = 'won' | 'lost' | 'unknown';

export function insightVerdict(insight: LoopInsight): InsightVerdict {
  if (insight.citations.some(isLossCitation)) return 'lost';
  if (insight.citations.some(isWinCitation)) return 'won';
  return 'unknown';
}
